/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Mic, MicOff, Languages } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import useWebSpeech from '@/lib/useWebSpeech';
import { parseDictation, SPEECH_LANG_TAGS } from '@/lib/dictationParser';

/**
 * DictationButton — mic button that lets the doctor dictate a prescription
 * line hands-free. Supports Italian, English and Spanish so foreign patients
 * can be served without switching interface language.
 *
 * The chosen language is persisted in localStorage under
 * `sanitanexus.dictation.lang` and defaults to Italian.
 *
 * Contract:
 *   • `onParsed(parsed)` fires each time a stable sentence is committed.
 *   • `parsed.lang` echoes the language actually used by the parser (useful
 *     for surfacing a badge in the parent form).
 */
const LANG_OPTIONS = [
  { code: 'it', label: 'Italiano', flag: '🇮🇹', shortLabel: 'IT' },
  { code: 'en', label: 'English', flag: '🇬🇧', shortLabel: 'EN' },
  { code: 'es', label: 'Español', flag: '🇪🇸', shortLabel: 'ES' },
];

const LANG_STORAGE_KEY = 'sanitanexus.dictation.lang';

const readSavedLang = () => {
  try {
    const v = typeof window !== 'undefined' ? window.localStorage?.getItem(LANG_STORAGE_KEY) : null;
    if (v && LANG_OPTIONS.some((o) => o.code === v)) return v;
  } catch (e) { /* localStorage may be blocked in kiosk / private mode */ }
  return 'it';
};

const TOAST_MSG = {
  it: { start: 'Parla ora — la dettatura è attiva', stop: 'Dettatura interrotta' },
  en: { start: 'Speak now — dictation is on', stop: 'Dictation stopped' },
  es: { start: 'Habla ahora — la dictado está activa', stop: 'Dictado detenido' },
};

const BUTTON_LABEL_LISTENING = {
  it: 'In ascolto…', en: 'Listening…', es: 'Escuchando…',
};

export const DictationButton = ({ onParsed, onRawText, mode = 'prescription', size = 'default', label = 'Detta ricetta', testId = 'dictate-btn' }) => {
  const [lang, setLang] = useState(readSavedLang);

  const {
    supported, listening, interim, start, stop, reset,
  } = useWebSpeech({
    lang: SPEECH_LANG_TAGS[lang] || 'it-IT',
    continuous: true,
    interim: true,
    onFinal: (chunk) => {
      if (mode === 'raw') {
        // No parsing — hand back the raw transcript (already trimmed by useWebSpeech).
        if (typeof onRawText === 'function') onRawText(chunk);
        return;
      }
      const parsed = parseDictation(chunk, { lang });
      if (typeof onParsed === 'function') onParsed(parsed);
    },
  });

  // Persist language choice
  useEffect(() => {
    try {
      window.localStorage?.setItem(LANG_STORAGE_KEY, lang);
    } catch (e) { /* ignore */ }
  }, [lang]);

  const changeLang = useCallback((next) => {
    if (listening) {
      // Stop the recognizer before switching — the new BCP-47 tag will be
      // picked up on the next `start()`.
      try { stop(); } catch (e) { /* ignore */ }
    }
    setLang(next);
  }, [listening, stop]);

  if (!supported) {
    return (
      <Button
        variant="outline"
        size={size}
        disabled
        title="Il tuo browser non supporta la dettatura vocale (usa Chrome / Edge / Safari)"
        data-testid={`${testId}-unsupported`}
      >
        <MicOff className="h-4 w-4 mr-2" /> Dettatura non disponibile
      </Button>
    );
  }

  const toggle = () => {
    if (listening) {
      stop();
      toast.info(TOAST_MSG[lang]?.stop || TOAST_MSG.it.stop);
    } else {
      reset();
      start();
      toast.info(TOAST_MSG[lang]?.start || TOAST_MSG.it.start);
    }
  };

  const current = LANG_OPTIONS.find((o) => o.code === lang) || LANG_OPTIONS[0];

  return (
    <div className="inline-flex flex-col items-start gap-1">
      <div className="inline-flex items-center gap-1">
        <Button
          onClick={toggle}
          variant={listening ? 'default' : 'outline'}
          size={size}
          data-testid={testId}
          className={listening ? 'animate-pulse' : ''}
          aria-label={listening ? 'Interrompi dettatura' : 'Avvia dettatura'}
        >
          {listening ? <Mic className="h-4 w-4 mr-2 text-red-500" /> : <Mic className="h-4 w-4 mr-2" />}
          {listening ? (BUTTON_LABEL_LISTENING[lang] || 'In ascolto…') : label}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size={size}
              data-testid={`${testId}-lang`}
              title={`Lingua dettatura: ${current.label}`}
              aria-label={`Lingua dettatura: ${current.label}`}
            >
              <Languages className="h-4 w-4 mr-1" />
              <span className="font-mono text-xs">{current.shortLabel}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44" data-testid={`${testId}-lang-menu`}>
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Lingua paziente
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {LANG_OPTIONS.map((opt) => (
              <DropdownMenuItem
                key={opt.code}
                onSelect={() => changeLang(opt.code)}
                data-testid={`${testId}-lang-${opt.code}`}
                className={opt.code === lang ? 'bg-primary/10 font-semibold' : ''}
              >
                <span className="mr-2 text-base leading-none">{opt.flag}</span>
                <span className="flex-1">{opt.label}</span>
                {opt.code === lang && (
                  <Badge variant="secondary" className="ml-2 text-[10px]">attiva</Badge>
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {listening && interim && (
        <Badge variant="outline" className="text-xs font-mono max-w-md truncate" data-testid={`${testId}-interim`}>
          “{interim}”
        </Badge>
      )}
    </div>
  );
};

export default DictationButton;
